import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Check, Loader2, TriangleAlert } from "lucide-react";
import type { HandspeedSensor } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useManualHandspeed, useSetManualHandspeed, type ManualMutationError } from "./useManualMutations.js";

export interface HandspeedPickerDialogProps {
  manual: { id: number; name: string };
  session: AuthSession | null;
  onClose: () => void;
}

/**
 * The hand-speed device picker (ADR-039 D7) — the legacy picker's `handspeedsensors` variant: every
 * `HandSpeedSensor` in the realm as a checkbox, sorted by slug, with the slug shown because that is what is printed
 * on the hardware and the description on hover.
 *
 * Layered OVER the details drawer, like {@link ManageReviewersDialog}. Self-contained: it reads the same query key
 * the drawer's section does, so a save updates both at once.
 *
 * Selection is staged and committed with Save, not toggled per checkbox: the write REPLACES the whole list in one
 * request (D5), so a per-tick save would fire one full replace per click and leave a half-built rig on a failure.
 */
export function HandspeedPickerDialog({ manual, session, onClose }: HandspeedPickerDialogProps) {
  const { t } = useTranslation();
  const rig = useManualHandspeed(session, manual.id, true);
  const save = useSetManualHandspeed(session);
  const [staged, setStaged] = useState<number[] | null>(null);

  const bound = rig.data?.sensors ?? [];
  const candidates = rig.data?.candidates ?? [];
  // The selection starts from what is bound. `staged === null` means "untouched", so a refetch that arrives before
  // the first click is still reflected rather than pinned to a stale snapshot.
  const selected = staged ?? bound.map((s) => s.id);

  /**
   * The rows: every candidate, PLUS any bound device the picker would never offer (D4). Production holds exactly one
   * — a `SwitchSensor` — and the legacy leaves such a row unremovable because its picker cannot see it. Here it is
   * listed, flagged, and unticking it is the way out.
   */
  const rows = useMemo(() => {
    const offered = new Set(candidates.map((c) => c.id));
    const strays = bound.filter((s) => !offered.has(s.id));
    return [...candidates, ...strays].sort((a, b) => a.slug.localeCompare(b.slug));
  }, [candidates, bound]);

  const toggle = (id: number) =>
    setStaged((cur) => {
      const base = cur ?? bound.map((s) => s.id);
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });

  const dirty = staged !== null && (staged.length !== bound.length || staged.some((id) => !bound.some((b) => b.id === id)));
  const error = save.error as ManualMutationError | undefined;

  const commit = () => {
    if (!rig.data) return;
    save.mutate({ id: manual.id, enabled: rig.data.enabled, sensorIds: selected }, { onSuccess: () => onClose() });
  };

  return (
    // Sealed while the PATCH is in flight: dismissing would unmount the picker mid-write and leave the section behind
    // it acting on pre-save data (diff panel). BOTH props are needed — in react-aria `isDismissable` governs the
    // BACKDROP, and Escape is `isKeyboardDismissDisabled`; setting only the first leaves the keyboard path wide open.
    <Dialog
      isOpen
      isDismissable={!save.isPending}
      isKeyboardDismissDisabled={save.isPending}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogTitle>{t("manualManagement.handspeed.title")}</DialogTitle>
      <DialogDescription>
        {t("manualManagement.handspeed.help")} <strong className="text-foreground">{manual.name}</strong>.
      </DialogDescription>

      <div className="max-h-[50vh] min-h-0 flex-1 overflow-y-auto">
        {rig.isPending ? (
          <p className="text-muted-foreground py-4 text-sm">{t("common.loading")}</p>
        ) : rig.isError ? (
          <p className="text-muted-foreground py-4 text-sm">{t("manualManagement.handspeed.loadFailed")}</p>
        ) : rows.length === 0 ? (
          // A realm with no hand-speed hardware at all — xr-area is exactly this. An empty grid would read as a
          // loading failure, so say what is actually true.
          <p className="text-muted-foreground py-4 text-sm">
            {t("manualManagement.handspeed.none")}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-1 py-1 sm:grid-cols-2">
            {rows.map((s) => (
              <li key={s.id}>
                <DeviceCheckbox
                  sensor={s}
                  isSelected={selected.includes(s.id)}
                  isDisabled={save.isPending}
                  onToggle={() => toggle(s.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p className="text-destructive text-sm">
          {error.code === "HANDSPEED_UNAVAILABLE"
            ? t("manualManagement.handspeed.deviceGone")
            : error.code === "MANUAL_BODY_STALE"
              ? t("manualManagement.handspeed.stale")
              : t("manualManagement.handspeed.saveFailed")}
        </p>
      ) : null}

      <DialogFooter>
        <Button variant="ghost" onPress={onClose} isDisabled={save.isPending}>
          {t("other.cancelButton")}
        </Button>
        <Button onPress={commit} isDisabled={!dirty || save.isPending || rig.isPending}>
          {save.isPending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
          {t("other.saveButtonLabel")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/**
 * One device row.
 *
 * An `aria-pressed` toggle button with a tick box — the repo's checkbox idiom (there is no `<input type=checkbox>`
 * primitive; see {@link InheritCategoriesChecklist}). The slug sits beside the name because it is what is printed on
 * the hardware, and the description is on hover, so this reads like ADR-038's element tiles.
 */
function DeviceCheckbox({
  sensor,
  isSelected,
  isDisabled,
  onToggle,
}: {
  sensor: HandspeedSensor;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const row = (
    <button
      type="button"
      aria-pressed={isSelected}
      aria-label={`${sensor.slug} ${sensor.displayName ?? ""}`.trim()}
      disabled={isDisabled}
      onClick={onToggle}
      className="hover:bg-muted focus-visible:ring-ring/50 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left outline-none transition-colors focus-visible:ring-2 disabled:opacity-50"
    >
      <span
        aria-hidden="true"
        className={cn(
          "grid size-4 shrink-0 place-items-center rounded border transition-colors",
          isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border text-transparent",
        )}
      >
        <Check className="size-3" />
      </span>
      <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-medium tabular-nums">
        {sensor.slug}
      </span>
      <span className="truncate text-sm">{sensor.displayName ?? sensor.slug}</span>
      {!sensor.isHandSpeed ? <TriangleAlert aria-hidden="true" className="text-attention ml-auto size-3.5 shrink-0" /> : null}
    </button>
  );
  const hint = !sensor.isHandSpeed
    ? t("manualManagement.handspeed.notHandspeed")
    : sensor.description;
  if (!hint) return row;
  return (
    <TooltipTrigger delay={0}>
      {row}
      <Tooltip>{hint}</Tooltip>
    </TooltipTrigger>
  );
}
